import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Sidebar from "../Dashboard/Sidebar";
import "../Dashboard/Dashboard.css";
import "./MissingPages.css";
import { ArrowLeft, Award, BookOpen, ChartLine, CheckCircle2, ChevronLeft, ChevronRight, ClipboardCheck, Clock3, Copy, ExternalLink, FileDown, Filter, Megaphone, MessageSquareText, NotebookPen, Plus, Search, Send, Settings2, Share2, Shield, SlidersHorizontal, TrendingUp, UserPlus, Users, XCircle } from "lucide-react";
import { adminApi, analyticsApi, feedbackApi, profileApi, submissionsApi, tracksApi } from "../../api/client";
import { getApiErrorMessage } from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { resolveMediaUrl } from "../../utils/media";
import { stripMarkdown } from "../../utils/markdown";
import { clearActiveTrackId, getActiveTrackId, setActiveTaskId, setActiveTrackId } from "../../utils/session";
import { getTrackInstructorNames, getTrackInstructors, isTrackOwnedByUser } from "../../utils/track";
import MarkdownPreview from "../Common/MarkdownPreview";

const roleLabel = { student: "Student", instructor: "Instructor", super_admin: "Super Admin" };
const formatDateTime = (v) => { try { return v ? new Date(v).toLocaleString() : "-"; } catch { return "-"; } };
const isHttpUrl = (value) => /^https?:\/\//i.test(String(value || ""));
const getAttachmentNameFromUrl = (url, fallback = "Attachment") => {
  try {
    const noQuery = `${url || ""}`.split("?")[0];
    const fileName = decodeURIComponent(noQuery.split("/").pop() || "").trim();
    return fileName || fallback;
  } catch {
    return fallback;
  }
};
const normalizeAnnouncementAttachmentItems = (attachments = []) =>
  (Array.isArray(attachments) ? attachments : [])
    .map((attachment, idx) => {
      const fallbackName = `Attachment ${idx + 1}`;

      if (attachment && typeof attachment === "object") {
        const rawOpenUrl = `${attachment.openUrl || attachment.url || attachment.href || ""}`.trim();
        const openUrl = rawOpenUrl ? resolveMediaUrl(rawOpenUrl) : "";
        const name = `${attachment.name || attachment.fileName || getAttachmentNameFromUrl(rawOpenUrl, fallbackName)}`.trim() || fallbackName;
        return { name, openUrl };
      }

      const rawValue = `${attachment || ""}`.trim();
      if (!rawValue) return null;

      const openUrl = isHttpUrl(rawValue) ? resolveMediaUrl(rawValue) : "";
      const name = openUrl ? getAttachmentNameFromUrl(rawValue, fallbackName) : rawValue;
      return { name, openUrl };
    })
    .filter(Boolean);

const Badge = ({ status }) => {
  const s = String(status || "").toLowerCase();
  let c = "status-neutral";
  if (s.includes("open") || s.includes("approved") || s.includes("graded") || s.includes("low")) c = "status-good";
  if (s.includes("pending") || s.includes("review") || s.includes("medium")) c = "status-warn";
  if (s.includes("closed") || s.includes("reject") || s.includes("high") || s.includes("late")) c = "status-danger";
  return <span className={`missing-status ${c}`}>{status}</span>;
};

const Shell = ({ title, subtitle, actions, children }) => {
  const { user } = useAuth();
  return (
    <div className="dashboard-container">
      <div className="dashboard-sidebar-wrapper"><Sidebar /></div>
      <div className="dashboard-main-wrapper missing-main">
        <header className="missing-header">
          <div><h1>{title}</h1><p>{subtitle}</p></div>
          <div className="missing-header-actions"><span className="missing-status status-neutral">{roleLabel[user?.role] || "User"}</span>{actions}</div>
        </header>
        {children}
      </div>
    </div>
  );
};

const useTrackScope = ({ mode = "all" } = {}) => {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [activeTrackId, setTrack] = useState("");
  const [tracksError, setTracksError] = useState("");
  const [loadingTracks, setLoadingTracks] = useState(false);

  const loadTracks = useCallback(async () => {
    setLoadingTracks(true); setTracksError("");
    try {
      const { data } = await tracksApi.list();
      const list = data?.tracks || [];
      const userId = user?.id || user?._id;

      let scopedTracks = list;
      if (user?.role === "student" && mode !== "all") {
        const profile = await profileApi.me();
        const approvedIds = new Set((profile.data?.profile?.tracks || []).map((t) => `${t?._id || t}`));
        scopedTracks = list.filter((track) => approvedIds.has(`${track._id}`));
      } else if (user?.role === "instructor") {
        if (mode === "owned" || mode === "accessible") {
          scopedTracks = list.filter((track) => isTrackOwnedByUser(track, userId));
        }
      }

      setTracks(scopedTracks);
      const saved = getActiveTrackId();
      const selected = scopedTracks.find((x) => x._id === saved) || scopedTracks[0] || null;
      if (selected) { setTrack(selected._id); setActiveTrackId(selected._id); } else { setTrack(""); clearActiveTrackId(); }
    } catch (e) { setTracksError(getApiErrorMessage(e, "Failed to load tracks")); }
    finally { setLoadingTracks(false); }
  }, [mode, user?.id, user?._id, user?.role]);

  useEffect(() => { loadTracks(); }, [loadTracks]);
  const activeTrack = useMemo(() => tracks.find((t) => t._id === activeTrackId) || null, [tracks, activeTrackId]);
  const onTrackChange = (e) => { setTrack(e.target.value); setActiveTrackId(e.target.value); };

  return { tracks, activeTrackId, activeTrack, onTrackChange, tracksError, loadingTracks, reloadTracks: loadTracks };
};

const TrackPicker = ({ tracks, value, onChange, disabled }) => (
  <select className="form-select form-select-sm" value={value || ""} onChange={onChange} disabled={disabled}>
    {tracks.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
  </select>
);

export const StudentDocumentationPage = () => {
  const journeySections = [
    {
      title: "1. Start From Tracks",
      icon: <BookOpen size={18} />,
      intro: "Open the Tracks page to explore available courses and request enrollment in the one you want.",
      steps: [
        "Go to `Tracks` from the sidebar or the top navigation.",
        "Browse the available tracks and open any track card to read its description.",
        "Press `Request Enrollment` to join a course.",
        "Wait for the instructor to approve your request before you can fully work inside that track.",
      ],
      tips: [
        "If the track is already approved for you, it will become part of your available student flow.",
        "If your request is still pending, keep checking announcements or your dashboard later.",
      ],
      actions: [
        { to: "/tracks", label: "Open Tracks" },
      ],
    },
    {
      title: "2. Follow Announcements",
      icon: <Megaphone size={18} />,
      intro: "Announcements are where instructors post updates, reminders, links, and important course notes.",
      steps: [
        "Open `Announcements` to see all announcements for the currently selected track.",
        "You can also see the latest announcements on the dashboard widget.",
        "Open any announcement to read the full content, links, and attachments.",
      ],
      tips: [
        "Always check announcements before deadlines because instructors may post clarifications or required resources.",
        "Use the track selector when you are enrolled in more than one track.",
      ],
      actions: [
        { to: "/announcements", label: "Open Announcements" },
        { to: "/dashboard", label: "Open Dashboard" },
      ],
    },
    {
      title: "3. Track Your Tasks",
      icon: <ClipboardCheck size={18} />,
      intro: "The Tasks page shows the assignments available for your selected track with deadlines and submission status.",
      steps: [
        "Open `Tasks` from the sidebar.",
        "Review task titles, points, difficulty, and deadline status.",
        "Use the `Details` button to read the full task description and check whether resubmission is allowed.",
      ],
      tips: [
        "Late or graded tasks are clearly marked so you can quickly understand your current status.",
        "Read the task description carefully because it may include instructions, links, and formatting requirements.",
      ],
      actions: [
        { to: "/tasks", label: "Open Tasks" },
      ],
    },
    {
      title: "4. Submit Your Task",
      icon: <Send size={18} />,
      intro: "The Submit Task page is where you upload your file, add a link, or write comments depending on the task rules.",
      steps: [
        "Open `Submit Task` from the sidebar.",
        "Choose the correct track first, then choose the task.",
        "Check the task deadline, points, submission type, and resources.",
        "Upload a file, add a link, or do both depending on the task requirements.",
        "Press `Submit Assignment` to send your work.",
      ],
      tips: [
        "If resubmission is allowed, you can submit again and the latest submission will be considered.",
        "If the task is locked, it means you already submitted it and resubmission is not allowed.",
      ],
      actions: [
        { to: "/assignment", label: "Open Submit Task" },
      ],
    },
    {
      title: "5. Check Grades And Feedback",
      icon: <Award size={18} />,
      intro: "You can follow your performance from the Profile page and from the task status inside the submission flow.",
      steps: [
        "Open `Profile` to see your task history.",
        "Look at the `Status` and `Grade` columns for each assignment.",
        "If feedback is available, press `View Feedback` to open the instructor notes.",
        "You can also open a task again from `Submit Task` to see whether it was graded and whether resubmission is still allowed.",
      ],
      tips: [
        "Grades appear after the instructor reviews your work.",
        "Feedback helps you understand mistakes and improve your next submission.",
      ],
      actions: [
        { to: "/profile", label: "Open Profile" },
        { to: "/assignment", label: "Check Submission Status" },
      ],
    },
    {
      title: "6. Use Feedback Page",
      icon: <MessageSquareText size={18} />,
      intro: "The Feedback page is where you can submit your own feedback to the system or the learning experience.",
      steps: [
        "Open `Feedback` from the sidebar.",
        "Write your message clearly and include anything helpful for the instructors or organizers.",
        "Submit the form once you finish.",
      ],
      tips: [
        "Use feedback to report issues, suggest improvements, or share your learning experience.",
      ],
      actions: [
        { to: "/feedback", label: "Open Feedback" },
      ],
    },
    {
      title: "7. Follow Your Rank",
      icon: <TrendingUp size={18} />,
      intro: "Leaderboard pages help you understand your performance compared with other students.",
      steps: [
        "Open `Leaderboard` to view rankings.",
        "Check your total points and compare your progress with top students.",
        "Use this as motivation to keep submitting tasks on time.",
      ],
      tips: [
        "Your score grows as tasks are graded and bonus points are added.",
      ],
      actions: [
        { to: "/leaderboard", label: "Open Leaderboard" },
      ],
    },
  ];

  return (
    <Shell
      title="Student Documentation"
      subtitle="A complete guide for students from joining a track to checking grades, feedback, announcements, and submissions."
    >
      <section className="missing-panel student-guide-hero">
        <div>
          <h2><BookOpen size={18} /> How To Use The Platform</h2>
          <p className="student-guide-hero-text">
            This guide explains the normal student journey inside the website: choose a track, request enrollment,
            follow announcements, read tasks, submit assignments, and review your grades and feedback.
          </p>
        </div>
        <div className="student-guide-quick-links">
          <Link to="/tracks" className="missing-btn primary">Start With Tracks</Link>
          <Link to="/assignment" className="missing-btn ghost">Open Submit Task</Link>
          <Link to="/profile" className="missing-btn ghost">Open Profile</Link>
        </div>
      </section>

      <section className="student-guide-grid">
        {journeySections.map((section) => (
          <article key={section.title} className="student-guide-card">
            <div className="student-guide-card-head">
              <div className="student-guide-icon">{section.icon}</div>
              <h3>{section.title}</h3>
            </div>
            <p className="student-guide-intro">{section.intro}</p>

            <div className="student-guide-block">
              <h4>Steps</h4>
              <ol className="student-guide-list ordered">
                {section.steps.map((step) => <li key={step}>{step}</li>)}
              </ol>
            </div>

            <div className="student-guide-block">
              <h4>Tips</h4>
              <ul className="student-guide-list">
                {section.tips.map((tip) => <li key={tip}>{tip}</li>)}
              </ul>
            </div>

            <div className="student-guide-actions">
              {section.actions.map((action) => (
                <Link key={action.to} to={action.to} className="missing-btn tiny ghost">
                  {action.label}
                </Link>
              ))}
            </div>
          </article>
        ))}
      </section>

      <section className="missing-panel student-guide-summary">
        <h2><CheckCircle2 size={18} /> Full Student Flow Summary</h2>
        <ul className="student-guide-list">
          <li>Go to `Tracks`, choose a course, and request enrollment.</li>
          <li>After approval, use the dashboard and announcements to stay updated.</li>
          <li>Open `Tasks` to see available assignments and read the details carefully.</li>
          <li>Use `Submit Task` to upload your work before the deadline.</li>
          <li>Check `Profile` to review grades, task history, and instructor feedback.</li>
          <li>Use `Feedback` if you want to send your own comments or suggestions.</li>
          <li>Track your progress from the `Leaderboard`.</li>
        </ul>
      </section>
    </Shell>
  );
};

export const EnrollmentRequestsPage = () => {
  const { tracks, activeTrackId, activeTrack, onTrackChange, tracksError, loadingTracks } = useTrackScope({ mode: "owned" });
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const getMemberIdentity = (entry) => entry?.student || entry?.user || entry || {};
  const getMemberId = (entry) => {
    const identity = getMemberIdentity(entry);
    return identity?._id || entry?.studentId || entry?.userId || entry?._id || "";
  };

  const loadEnrollmentData = useCallback(async () => {
    if (!activeTrackId) {
      setRows([]);
      setMembers([]);
      return;
    }

    setError("");
    try {
      const [requestsRes, membersRes] = await Promise.all([
        tracksApi.requests(activeTrackId),
        tracksApi.members(activeTrackId),
      ]);
      setRows(requestsRes.data?.requests || []);
      setMembers(membersRes.data?.members || []);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load enrollments"));
    }
  }, [activeTrackId]);

  useEffect(() => {
    loadEnrollmentData();
  }, [loadEnrollmentData]);

  const decide = async (id, action) => {
    setError(""); setMsg("");
    try {
      await tracksApi.decideEnrollment(id, action);
      await loadEnrollmentData();
      setMsg(`Enrollment ${action}d.`);
    }
    catch (e) { setError(getApiErrorMessage(e, "Could not update enrollment")); }
  };

  const removeMember = async (member) => {
    const memberIdentity = getMemberIdentity(member);
    const memberId = getMemberId(member);
    if (!activeTrackId || !memberId) {
      setError("Could not determine which member to remove.");
      return;
    }

    const confirmed = window.confirm(`Remove ${memberIdentity?.fullName || memberIdentity?.email || "this member"} from ${activeTrack?.name || "this track"}?`);
    if (!confirmed) return;

    setError("");
    setMsg("");
    try {
      await tracksApi.removeMember(activeTrackId, memberId);
      setMembers((prev) => prev.filter((entry) => `${getMemberId(entry)}` !== `${memberId}`));
      setMsg("Member removed successfully.");
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not remove member"));
    }
  };

  return (
    <Shell title="Enrollment Requests Management" subtitle="Review and decide pending requests." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {tracksError ? <div className="alert alert-danger py-2">{tracksError}</div> : null}
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {msg ? <div className="alert alert-success py-2">{msg}</div> : null}
      <div className="missing-two-col">
        <section className="missing-panel">
          <h2>{activeTrack?.name || "Track"} - Pending</h2>
          <div className="missing-table-wrap"><table className="missing-table"><thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Action</th></tr></thead><tbody>
            {rows.length ? rows.map((r) => <tr key={r._id}><td>{r.student?.fullName}</td><td>{r.student?.email}</td><td><Badge status={r.status} /></td><td className="missing-action-group"><button className="missing-btn tiny" onClick={() => decide(r._id, "approve")}>Approve</button><button className="missing-btn tiny danger" onClick={() => decide(r._id, "reject")}>Reject</button></td></tr>) : <tr><td colSpan="4" className="text-center text-muted py-4">No pending requests.</td></tr>}
          </tbody></table></div>
        </section>

        <section className="missing-panel">
          <h2>{activeTrack?.name || "Track"} - Approved Members</h2>
          <div className="missing-table-wrap"><table className="missing-table"><thead><tr><th>Name</th><th>Email</th><th>Status</th><th>Action</th></tr></thead><tbody>
            {members.length ? members.map((member) => {
              const memberIdentity = getMemberIdentity(member);
              const memberId = getMemberId(member);
              return (
                <tr key={memberId || member._id || memberIdentity?.email}>
                  <td>{memberIdentity?.fullName || "-"}</td>
                  <td>{memberIdentity?.email || "-"}</td>
                  <td><Badge status="approved" /></td>
                  <td className="missing-action-group">
                    <button className="missing-btn tiny danger" onClick={() => removeMember(member)}>Remove</button>
                  </td>
                </tr>
              );
            }) : <tr><td colSpan="4" className="text-center text-muted py-4">No approved members yet.</td></tr>}
          </tbody></table></div>
        </section>
      </div>
    </Shell>
  );
};

const useCreateAnnouncementTaskState = () => {
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [a, setA] = useState({ title: "", content: "", linksText: "", files: [] });
  const [announcementFileInputKey, setAnnouncementFileInputKey] = useState(0);
  const [taskFileInputKey, setTaskFileInputKey] = useState(0);
  const [t, setT] = useState({
    title: "",
    description: "",
    deadline: "",
    points: 100,
    difficulty: "medium",
    submissionType: "file_or_link",
    allowResubmission: true,
    resourceLinksText: "",
    resourceFiles: [],
  });
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const createAnn = async () => {
    setError("");
    setMsg("");
    if (!activeTrackId) {
      setError("Please select a track first.");
      return;
    }

    const links = a.linksText.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
    const invalidLink = links.find((link) => !isHttpUrl(link));
    if (invalidLink) {
      setError("Each link must start with http:// or https://");
      return;
    }

    const formData = new FormData();
    formData.append("title", a.title.trim());
    formData.append("content", a.content.trim());
    links.forEach((link) => formData.append("links", link));
    a.files.forEach((file) => formData.append("files", file));

    try {
      await tracksApi.createAnnouncement(activeTrackId, formData);
      setA({ title: "", content: "", linksText: "", files: [] });
      setAnnouncementFileInputKey((prev) => prev + 1);
      setMsg("Announcement created.");
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to create announcement"));
    }
  };
  const createTask = async () => {
    setError("");
    setMsg("");
    if (!activeTrackId) {
      setError("Please select a track first.");
      return;
    }

    const resourceLinks = t.resourceLinksText.split(/\r?\n/).map((x) => x.trim()).filter(Boolean);
    const invalidLink = resourceLinks.find((link) => !isHttpUrl(link));
    if (invalidLink) {
      setError("Each task resource link must start with http:// or https://");
      return;
    }

    const formData = new FormData();
    formData.append("title", t.title.trim());
    formData.append("description", t.description.trim());
    formData.append("deadline", t.deadline);
    formData.append("points", String(Number(t.points)));
    formData.append("difficulty", t.difficulty);
    formData.append("submissionType", t.submissionType);
    formData.append("allowResubmission", String(Boolean(t.allowResubmission)));
    resourceLinks.forEach((link) => formData.append("resourceLinks", link));
    t.resourceFiles.forEach((file) => formData.append("files", file));

    try {
      await tracksApi.createTask(activeTrackId, formData);
      setT({
        title: "",
        description: "",
        deadline: "",
        points: 100,
        difficulty: "medium",
        submissionType: "file_or_link",
        allowResubmission: true,
        resourceLinksText: "",
        resourceFiles: [],
      });
      setTaskFileInputKey((prev) => prev + 1);
      setMsg("Task created.");
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to create task"));
    }
  };

  return {
    tracks,
    activeTrackId,
    onTrackChange,
    loadingTracks,
    a,
    setA,
    announcementFileInputKey,
    t,
    setT,
    taskFileInputKey,
    error,
    msg,
    createAnn,
    createTask,
  };
};

const AnnouncementForm = ({ a, setA, announcementFileInputKey, createAnn }) => (
  <section className="missing-panel">
    <h2><Megaphone size={18} /> Announcement</h2>
    <div className="missing-form-grid one-col">
      <label>Title<input value={a.title} onChange={(e) => setA((p) => ({ ...p, title: e.target.value }))} /></label>
      <div className="markdown-editor-field">
        <div className="markdown-editor-label-row">
          <label className="mb-0">Content</label>
          <small>Write in Markdown like a GitHub README.</small>
        </div>
        <div className="markdown-editor-split">
          <textarea
            rows="14"
            value={a.content}
            onChange={(e) => setA((p) => ({ ...p, content: e.target.value }))}
            placeholder={"# Announcement title\n\nAdd **formatted** content, lists, links, and code blocks."}
          />
          <div className="markdown-preview-shell">
            <div className="markdown-preview-shell-head">Preview</div>
            <MarkdownPreview content={a.content} emptyText="Markdown preview will appear here." />
          </div>
        </div>
      </div>
      <label>Links (one per line)<textarea rows="3" placeholder="https://example.com/resource" value={a.linksText} onChange={(e) => setA((p) => ({ ...p, linksText: e.target.value }))} /></label>
      <label>Attachments (multiple)<input key={announcementFileInputKey} type="file" multiple onChange={(e) => setA((p) => ({ ...p, files: Array.from(e.target.files || []) }))} /></label>
      {a.files.length ? <small className="announcement-files-hint">{a.files.length} file(s) selected.</small> : null}
      <button className="missing-btn primary" onClick={createAnn}><Send size={16} /> Publish</button>
    </div>
  </section>
);

const TaskForm = ({ t, setT, taskFileInputKey, createTask }) => (
  <section className="missing-panel">
    <h2><ClipboardCheck size={18} /> Task</h2>
    <div className="missing-form-grid one-col">
      <label>Title<input value={t.title} onChange={(e) => setT((p) => ({ ...p, title: e.target.value }))} /></label>
      <div className="markdown-editor-field">
        <div className="markdown-editor-label-row">
          <label className="mb-0">Description</label>
          <small>Supports headings, bold text, lists, links, and code blocks.</small>
        </div>
        <div className="markdown-editor-split">
          <textarea
            rows="14"
            value={t.description}
            onChange={(e) => setT((p) => ({ ...p, description: e.target.value }))}
            placeholder={"## Task Overview\n\n- Requirements\n- Steps\n- Submission notes"}
          />
          <div className="markdown-preview-shell">
            <div className="markdown-preview-shell-head">Preview</div>
            <MarkdownPreview content={t.description} emptyText="Task description preview will appear here." />
          </div>
        </div>
      </div>
      <label>Deadline<input type="datetime-local" value={t.deadline} onChange={(e) => setT((p) => ({ ...p, deadline: e.target.value }))} /></label>
      <label>Points<input type="number" value={t.points} onChange={(e) => setT((p) => ({ ...p, points: e.target.value }))} /></label>
      <label>Difficulty<select value={t.difficulty} onChange={(e) => setT((p) => ({ ...p, difficulty: e.target.value }))}><option value="easy">easy</option><option value="medium">medium</option><option value="hard">hard</option></select></label>
      <label>Submission Mode<select value={t.submissionType} onChange={(e) => setT((p) => ({ ...p, submissionType: e.target.value }))}><option value="file">File only</option><option value="drive_link">Link only</option><option value="file_or_link">File or link</option></select></label>
      <label>Task Resource Links (one per line)<textarea rows="3" placeholder="https://example.com/file.pdf" value={t.resourceLinksText} onChange={(e) => setT((p) => ({ ...p, resourceLinksText: e.target.value }))} /></label>
      <label>Task Resource Files (images/files)<input key={taskFileInputKey} type="file" multiple onChange={(e) => setT((p) => ({ ...p, resourceFiles: Array.from(e.target.files || []) }))} /></label>
      {t.resourceFiles.length ? <small className="announcement-files-hint">{t.resourceFiles.length} file(s) selected for task resources.</small> : null}
      <label className="d-flex align-items-center gap-2"><input type="checkbox" checked={t.allowResubmission} onChange={(e) => setT((p) => ({ ...p, allowResubmission: e.target.checked }))} /> Allow resubmission</label>
      <button className="missing-btn primary" onClick={createTask}><Plus size={16} /> Create</button>
    </div>
  </section>
);

export const CreateAnnouncementPage = () => {
  const { tracks, activeTrackId, onTrackChange, loadingTracks, a, setA, announcementFileInputKey, error, msg, createAnn } = useCreateAnnouncementTaskState();

  return (
    <Shell title="Create Announcement" subtitle="Publish updates for your selected track." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {msg ? <div className="alert alert-success py-2">{msg}</div> : null}
      <AnnouncementForm a={a} setA={setA} announcementFileInputKey={announcementFileInputKey} createAnn={createAnn} />
    </Shell>
  );
};

export const CreateTaskPage = () => {
  const { tracks, activeTrackId, onTrackChange, loadingTracks, t, setT, taskFileInputKey, error, msg, createTask } = useCreateAnnouncementTaskState();

  return (
    <Shell title="Create Task" subtitle="Publish a new task for your selected track." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {msg ? <div className="alert alert-success py-2">{msg}</div> : null}
      <TaskForm t={t} setT={setT} taskFileInputKey={taskFileInputKey} createTask={createTask} />
    </Shell>
  );
};

export const AnalyticsDashboardPage = () => {
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [a, setA] = useState(null); const [error, setError] = useState("");
  useEffect(() => { (async () => {
    if (!activeTrackId) return;
    try { const { data } = await analyticsApi.instructorTrack(activeTrackId); setA(data?.analytics || null); }
    catch (e) { setError(getApiErrorMessage(e, "Failed to load analytics")); }
  })(); }, [activeTrackId]);
  return (
    <Shell title="Analytics Dashboard" subtitle="Track metrics for selected track." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <section className="missing-stat-grid">
        <article className="missing-stat-card"><div className="missing-stat-icon"><Users size={18} /></div><div><p>Members</p><h3>{a?.approvedMembers ?? 0}</h3></div></article>
        <article className="missing-stat-card"><div className="missing-stat-icon"><ChartLine size={18} /></div><div><p>Completion</p><h3>{a?.completionRate ?? 0}%</h3></div></article>
        <article className="missing-stat-card"><div className="missing-stat-icon"><TrendingUp size={18} /></div><div><p>Avg Grade</p><h3>{a?.avgGrade ?? 0}</h3></div></article>
      </section>
    </Shell>
  );
};

export const SuperAdminControlPanelPage = () => {
  const [tracks, setTracks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [enrollmentCounts, setEnrollmentCounts] = useState({});
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", status: "open", imageFile: null, bannerFile: null });
  const [editingTrackId, setEditingTrackId] = useState("");
  const [editForm, setEditForm] = useState({ name: "", description: "", status: "open", imageFile: null, bannerFile: null });
  const [assignment, setAssignment] = useState({ trackId: "", email: "" });
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  const assignedInstructors = useMemo(() => {
    const map = new Map();
    tracks.forEach((track) => {
      getTrackInstructors(track).forEach((instructor) => {
        const id = instructor?._id || instructor?.id;
        if (!id || map.has(id)) return;
        map.set(id, {
          id,
          fullName: instructor.fullName || "Instructor",
          email: instructor.email || "",
        });
      });
    });
    return Array.from(map.values());
  }, [tracks]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [tracksRes, logsRes] = await Promise.all([
        tracksApi.list(),
        adminApi.auditLogs().catch(() => ({ data: { logs: [] } })),
      ]);

      const list = tracksRes.data?.tracks || [];
      setTracks(list);
      setLogs(logsRes.data?.logs || []);

      setAssignment((prev) => ({
        ...prev,
        trackId:
          prev.trackId && list.some((track) => `${track._id}` === `${prev.trackId}`)
            ? prev.trackId
            : list[0]?._id || "",
      }));

      const [requestsByTrack, membersByTrack] = await Promise.all([
        Promise.all(
          list.map(async (track) => {
            try {
              const { data } = await tracksApi.requests(track._id);
              return { trackId: track._id, count: (data?.requests || []).length };
            } catch {
              return { trackId: track._id, count: 0 };
            }
          })
        ),
        Promise.all(
          list.map(async (track) => {
            try {
              const { data } = await tracksApi.members(track._id);
              return { trackId: track._id, count: (data?.members || []).length };
            } catch {
              return { trackId: track._id, count: 0 };
            }
          })
        ),
      ]);

      const pendingTotal = requestsByTrack.reduce((sum, row) => sum + row.count, 0);
      setPendingApprovals(pendingTotal);

      const nextEnrollmentCounts = {};
      membersByTrack.forEach((row) => {
        nextEnrollmentCounts[row.trackId] = row.count;
      });
      setEnrollmentCounts(nextEnrollmentCounts);
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to load admin data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createTrack = async () => {
    setError("");
    setMsg("");
    try {
      const payload = new FormData();
      payload.append("name", form.name);
      payload.append("description", form.description);
      payload.append("status", form.status);
      if (form.imageFile) payload.append("image", form.imageFile);
      if (form.bannerFile) payload.append("banner", form.bannerFile);

      await adminApi.createTrack(payload);
      setMsg("Track created successfully.");
      setShowCreateForm(false);
      setForm({ name: "", description: "", status: "open", imageFile: null, bannerFile: null });
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to create track"));
    }
  };

  const startEditTrack = (track) => {
    setShowCreateForm(false);
    setEditingTrackId(track._id);
    setEditForm({
      name: track.name || "",
      description: track.description || "",
      status: track.status || "open",
      imageFile: null,
      bannerFile: null,
    });
    setError("");
    setMsg("");
  };

  const cancelEditTrack = () => {
    setEditingTrackId("");
    setEditForm({ name: "", description: "", status: "open", imageFile: null, bannerFile: null });
  };

  const saveTrackEdits = async () => {
    if (!editingTrackId) return;

    setError("");
    setMsg("");
    try {
      const payload = new FormData();
      payload.append("name", editForm.name);
      payload.append("description", editForm.description);
      payload.append("status", editForm.status);
      if (editForm.imageFile) payload.append("image", editForm.imageFile);
      if (editForm.bannerFile) payload.append("banner", editForm.bannerFile);

      const currentTrack = tracks.find((track) => `${track._id}` === `${editingTrackId}`);
      await adminApi.updateTrack(editingTrackId, payload);
      setMsg(`Track "${currentTrack?.name || editForm.name}" updated successfully.`);
      cancelEditTrack();
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update track"));
    }
  };

  const assignInstructor = async () => {
    const normalizedEmail = assignment.email.trim().toLowerCase();

    if (!assignment.trackId || !normalizedEmail) {
      setError("Please select a track and enter instructor email first.");
      return;
    }
    setError("");
    setMsg("");
    try {
      const payload = { email: normalizedEmail };
      const { data } = await adminApi.assignInstructor(assignment.trackId, payload);
      setMsg(
        data?.promoted
          ? "User promoted to instructor and assigned to this track successfully."
          : "Instructor assigned successfully."
      );
      setAssignment((prev) => ({ ...prev, email: "" }));
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to assign instructor"));
    }
  };

  const setTrackStatus = async (track, nextStatus) => {
    setError("");
    setMsg("");
    try {
      await adminApi.updateTrack(track._id, { status: nextStatus });
      setMsg(`Track "${track.name}" ${nextStatus === "closed" ? "closed" : "opened"} successfully.`);
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to update track status"));
    }
  };

  const deleteTrack = async (track) => {
    const confirmed = window.confirm(`Are you sure you want to delete "${track.name}"? This action cannot be undone.`);
    if (!confirmed) return;

    setError("");
    setMsg("");
    try {
      await adminApi.deleteTrack(track._id);
      setMsg(`Track "${track.name}" deleted successfully.`);
      setAssignment((prev) => (
        prev.trackId === track._id
          ? { ...prev, trackId: "", email: "" }
          : prev
      ));
      await load();
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to delete track"));
    }
  };

  const stats = useMemo(() => ({
    totalTracks: tracks.length,
    activeInstructors: assignedInstructors.length,
    pendingApprovals,
  }), [assignedInstructors.length, pendingApprovals, tracks.length]);

  return (
    <Shell title="Management / Tracks" subtitle="Manage tracks, instructors assignment, and approvals." actions={<button className="missing-btn ghost" onClick={load} disabled={loading}>{loading ? "Refreshing..." : "Refresh"}</button>}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {msg ? <div className="alert alert-success py-2">{msg}</div> : null}

      <section className="admin-track-cards">
        <article className="admin-stat-card">
          <p>Total Tracks</p>
          <h3>{stats.totalTracks}</h3>
        </article>
        <article className="admin-stat-card">
          <p>Active Instructors</p>
          <h3>{stats.activeInstructors}</h3>
          <small>Across {stats.totalTracks} track(s)</small>
        </article>
        <article className="admin-stat-card warn">
          <p>Pending Approvals</p>
          <h3>{stats.pendingApprovals}</h3>
          <small>Requires attention</small>
        </article>
      </section>

      <section className="missing-panel">
        <h2><Shield size={18} /> Super Admin Track Controls</h2>
        <p className="text-muted mb-3">
          You can open any instructor workflow from here, then switch between tracks using the selector inside each page.
        </p>
        <div className="admin-shortcuts-grid">
          <Link to="/enrollment-requests" className="missing-btn ghost">Manage Enrollments</Link>
          <Link to="/create-announcement" className="missing-btn ghost">Create Announcement</Link>
          <Link to="/create-task" className="missing-btn ghost">Create Task</Link>
          <Link to="/tasks-submitted" className="missing-btn ghost">View Submissions</Link>
          <Link to="/grading-interface" className="missing-btn ghost">Open Grading</Link>
          <Link to="/notification-center" className="missing-btn ghost">Notification Center</Link>
          <Link to="/analytics-dashboard" className="missing-btn ghost">Track Analytics</Link>
          <Link to="/track-social-preview" className="missing-btn ghost">Social Share</Link>
          <Link to="/course-basics" className="missing-btn ghost">Course Basics</Link>
          <Link to="/course-settings" className="missing-btn ghost">Course Settings</Link>
          <Link to="/course-curriculum" className="missing-btn ghost">Course Curriculum</Link>
          <Link to="/course-preview" className="missing-btn ghost">Course Preview</Link>
          <Link to="/course-final" className="missing-btn ghost">Course Final</Link>
        </div>
      </section>

      <section className="missing-panel admin-assignment-panel">
        <h2><UserPlus size={18} /> Instructors Assignment</h2>
        <p className="text-muted mb-3">Select track and enter an instructor email.</p>
        <div className="admin-assignment-grid">
          <label>
            Select Track
            <select value={assignment.trackId} onChange={(e) => setAssignment((prev) => ({ ...prev, trackId: e.target.value }))}>
              {tracks.map((track) => <option key={track._id} value={track._id}>{track.name}</option>)}
            </select>
          </label>
          <label>
            Instructor Email
            <input
              type="email"
              placeholder="name@example.com"
              value={assignment.email}
              onChange={(e) => setAssignment((prev) => ({ ...prev, email: e.target.value }))}
            />
          </label>
          <div className="admin-assignment-action">
            <button
              className="missing-btn primary"
              onClick={assignInstructor}
              disabled={!assignment.trackId || !assignment.email.trim()}
            >
              <UserPlus size={16} /> Assign
            </button>
          </div>
        </div>
        <small className="admin-assignment-helper text-muted">
          You can add more than one instructor to the same track. If this user is not instructor, they will be promoted and added.
        </small>
      </section>

      <section className="missing-panel admin-track-table-panel">
        <div className="admin-table-header">
          <div>
            <h2 className="mb-1">All Tracks</h2>
            <p className="text-muted mb-0">Manage and organize learning paths.</p>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            <button className="missing-btn ghost" disabled><Filter size={14} /> Filter</button>
            <button className="missing-btn primary" onClick={() => {
              cancelEditTrack();
              setShowCreateForm((prev) => !prev);
            }}>
              <Plus size={14} /> {showCreateForm ? "Close Form" : "New Track"}
            </button>
          </div>
        </div>

        {showCreateForm ? (
          <div className="admin-create-track-form">
            <div className="missing-form-grid two-col">
              <label>
                Track Name
                <input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
              </label>
              <label>
                Status
                <select value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
                  <option value="open">open</option>
                  <option value="closed">closed</option>
                </select>
              </label>
              <label className="full-width">
                Description
                <textarea rows="3" value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
              </label>
              <label>
                Track Image
                <input type="file" accept="image/*" onChange={(e) => setForm((prev) => ({ ...prev, imageFile: e.target.files?.[0] || null }))} />
                {form.imageFile ? <small>{form.imageFile.name}</small> : null}
              </label>
              <label>
                Track Banner
                <input type="file" accept="image/*" onChange={(e) => setForm((prev) => ({ ...prev, bannerFile: e.target.files?.[0] || null }))} />
                {form.bannerFile ? <small>{form.bannerFile.name}</small> : null}
              </label>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button className="missing-btn primary" onClick={createTrack}>Create Track</button>
            </div>
          </div>
        ) : null}

        {editingTrackId ? (
          <div className="admin-create-track-form">
            <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap mb-3">
              <div>
                <h3 className="mb-1">Edit Track</h3>
                <p className="text-muted mb-0">Update track details, banner, and cover image from the admin page.</p>
              </div>
              <button className="missing-btn ghost" onClick={cancelEditTrack}>Cancel</button>
            </div>
            <div className="missing-form-grid two-col">
              <label>
                Track Name
                <input value={editForm.name} onChange={(e) => setEditForm((prev) => ({ ...prev, name: e.target.value }))} />
              </label>
              <label>
                Status
                <select value={editForm.status} onChange={(e) => setEditForm((prev) => ({ ...prev, status: e.target.value }))}>
                  <option value="open">open</option>
                  <option value="closed">closed</option>
                </select>
              </label>
              <label className="full-width">
                Description
                <textarea rows="4" value={editForm.description} onChange={(e) => setEditForm((prev) => ({ ...prev, description: e.target.value }))} />
              </label>
              <label>
                Replace Track Image
                <input type="file" accept="image/*" onChange={(e) => setEditForm((prev) => ({ ...prev, imageFile: e.target.files?.[0] || null }))} />
                {editForm.imageFile ? <small>{editForm.imageFile.name}</small> : <small>Leave empty to keep the current image.</small>}
              </label>
              <label>
                Replace Track Banner
                <input type="file" accept="image/*" onChange={(e) => setEditForm((prev) => ({ ...prev, bannerFile: e.target.files?.[0] || null }))} />
                {editForm.bannerFile ? <small>{editForm.bannerFile.name}</small> : <small>Leave empty to keep the current banner.</small>}
              </label>
            </div>
            <div className="d-flex justify-content-end mt-3">
              <button className="missing-btn primary" onClick={saveTrackEdits}>Save Changes</button>
            </div>
          </div>
        ) : null}

        <div className="missing-table-wrap">
          <table className="missing-table">
            <thead>
              <tr>
                <th>Track Name</th>
                <th>Instructors</th>
                <th>Status</th>
                <th>Enrollments</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tracks.length ? tracks.map((track) => (
                <tr key={track._id}>
                  <td>
                    <div className="d-grid">
                      <strong>{track.name}</strong>
                      <small className="text-muted">Created {formatDateTime(track.createdAt)}</small>
                    </div>
                  </td>
                  <td>{getTrackInstructorNames(track).join(", ") || "Unassigned"}</td>
                  <td><Badge status={track.status} /></td>
                  <td>{enrollmentCounts[track._id] ?? 0}</td>
                  <td className="missing-action-group">
                    <button className="missing-btn tiny" onClick={() => startEditTrack(track)}>Edit</button>
                    <button className="missing-btn tiny danger" onClick={() => deleteTrack(track)}>Delete</button>
                    <button className="missing-btn tiny ghost" onClick={() => setTrackStatus(track, track.status === "open" ? "closed" : "open")}>{track.status === "open" ? "Close" : "Open"}</button>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">No tracks found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="missing-panel">
        <h2>Recent Admin Actions</h2>
        <div className="missing-feed">
          {logs.slice(0, 4).map((log) => (
            <article key={log._id} className="missing-feed-item">
              <div>
                <h3>{log.action}</h3>
                <p>{log.targetType} - {log.targetId}</p>
                <small>{formatDateTime(log.createdAt)}</small>
              </div>
            </article>
          ))}
          {!logs.length ? <div className="text-muted">No admin logs yet.</div> : null}
        </div>
      </section>
    </Shell>
  );
};

export const InstructorNotificationCenterPage = () => {
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [rows, setRows] = useState([]); const [error, setError] = useState(""); const [notice, setNotice] = useState("");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const attachmentItems = useMemo(
    () => normalizeAnnouncementAttachmentItems(selectedAnnouncement?.attachments || []),
    [selectedAnnouncement]
  );
  useEffect(() => { (async () => {
    setError("");
    setNotice("");
    if (!activeTrackId) { setRows([]); setNotice("No track assigned to this instructor account."); return; }
    try {
      const [requestsRes, feedbackRes, announcementsRes] = await Promise.all([tracksApi.requests(activeTrackId), feedbackApi.list(), tracksApi.announcements(activeTrackId)]);
      const requestRows = (requestsRes.data?.requests || []).map((x) => ({ id: `r-${x._id}`, type: "request", title: "Enrollment Request", detail: x.student?.fullName, time: x.createdAt, status: "Pending" }));
      const feedbackRows = (feedbackRes.data?.feedback || []).slice(0, 6).map((x) => ({ id: `f-${x._id}`, type: "feedback", title: `Feedback: ${x.category}`, detail: x.message, time: x.createdAt, status: "Review" }));
      const announcementRows = (announcementsRes.data?.announcements || []).slice(0, 6).map((x) => ({
        id: `a-${x._id}`,
        type: "announcement",
        title: `Announcement: ${x.title}`,
        detail: stripMarkdown(x.content) || "No content.",
        time: x.createdAt,
        status: "New",
        announcement: x,
      }));
      const nextRows = [...requestRows, ...feedbackRows, ...announcementRows].sort((x, y) => new Date(y.time) - new Date(x.time));
      setRows(nextRows);
      if (!nextRows.length) setNotice("No notifications yet.");
    } catch (e) { setError(getApiErrorMessage(e, "Failed to load notifications")); }
  })(); }, [activeTrackId]);

  return (
    <Shell title="Instructor Notification Center" subtitle="Unified feed from requests and feedback." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {notice ? <div className="alert alert-warning py-2">{notice}</div> : null}
      <section className="missing-panel">
        <div className="missing-feed">
          {rows.length ? rows.map((n) => (
            <article
              key={n.id}
              className={`missing-feed-item ${n.type === "announcement" ? "announcement-clickable" : ""}`}
              onClick={n.type === "announcement" ? () => setSelectedAnnouncement(n.announcement) : undefined}
              onKeyDown={n.type === "announcement" ? (event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setSelectedAnnouncement(n.announcement);
                }
              } : undefined}
              role={n.type === "announcement" ? "button" : undefined}
              tabIndex={n.type === "announcement" ? 0 : undefined}
            >
              <div>
                <h3>{n.title}</h3>
                <p className={n.type === "announcement" ? "announcement-preview-text" : ""}>{n.detail}</p>
                <small>{formatDateTime(n.time)}</small>
              </div>
              <Badge status={n.status} />
            </article>
          )) : <div className="text-muted">No notifications.</div>}
        </div>
      </section>

      {selectedAnnouncement ? (
        <div className="announcement-details-backdrop" onClick={() => setSelectedAnnouncement(null)}>
          <div className="announcement-details-card" onClick={(e) => e.stopPropagation()}>
            <header className="announcement-details-head">
              <div>
                <h3>{selectedAnnouncement.title}</h3>
                <small>{formatDateTime(selectedAnnouncement.createdAt)}</small>
              </div>
              <button type="button" className="missing-btn tiny ghost" onClick={() => setSelectedAnnouncement(null)}>Close</button>
            </header>

            <section className="announcement-details-section">
              <h4>Content</h4>
              <MarkdownPreview content={selectedAnnouncement.content} emptyText="No content." />
            </section>

            {Array.isArray(selectedAnnouncement.links) && selectedAnnouncement.links.length ? (
              <section className="announcement-details-section">
                <h4>Links</h4>
                <ul className="announcement-links-list">
                  {selectedAnnouncement.links.map((linkValue, idx) => (
                    <li key={`${linkValue}-${idx}`}>
                      <a href={resolveMediaUrl(linkValue)} target="_blank" rel="noreferrer">{resolveMediaUrl(linkValue)}</a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {attachmentItems.length ? (
              <section className="announcement-details-section">
                <h4>Attachments</h4>
                <ul className="announcement-links-list">
                  {attachmentItems.map((attachmentItem, idx) => (
                    <li key={`${attachmentItem.openUrl || attachmentItem.name}-${idx}`}>
                      {attachmentItem.openUrl ? (
                        <a href={attachmentItem.openUrl} target="_blank" rel="noreferrer">{attachmentItem.name}</a>
                      ) : (
                        <span>{attachmentItem.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </Shell>
  );
};

export const TrackSocialSharePreviewPage = () => {
  const { tracks, activeTrackId, activeTrack, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [shareMessage, setShareMessage] = useState("");
  const link = activeTrackId ? `${window.location.origin}/tracks?trackId=${activeTrackId}` : "-";
  const trackName = activeTrack?.name || "Track";
  const description = activeTrack?.description || "No description.";
  const bannerUrl = resolveMediaUrl(activeTrack?.bannerUrl);
  const avatarUrl = resolveMediaUrl(activeTrack?.imageUrl);
  const instructorName = getTrackInstructorNames(activeTrack).join(", ") || "Not assigned yet";
  const initials = trackName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("") || "TR";

  const copyLink = async () => {
    if (!activeTrackId) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = link;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setShareMessage("Link copied.");
    } catch {
      setShareMessage("Unable to copy link.");
    }
  };

  const openTrack = () => {
    if (!activeTrackId) return;
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleShare = async () => {
    if (!activeTrackId) return;
    if (navigator?.share) {
      try {
        await navigator.share({
          title: `${trackName} | IEEE Study Groups`,
          text: description,
          url: link,
        });
        setShareMessage("Shared successfully.");
        return;
      } catch (error) {
        if (error?.name === "AbortError") return;
      }
    }
    await copyLink();
  };

  useEffect(() => {
    setShareMessage("");
  }, [activeTrackId]);

  return (
    <Shell title="Track Social Share Preview" subtitle="Preview share card and link." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <div className="missing-two-col track-share-layout">
        <section className="missing-panel track-share-preview-panel">
          <article className="track-share-card">
            <div className="track-share-banner" style={bannerUrl ? { backgroundImage: `url(${bannerUrl})` } : undefined}>
              <Badge status={activeTrack?.status || "open"} />
            </div>
            <div className="track-share-body">
              <div className="track-share-head">
                <div className="track-share-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={trackName} />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
                <div>
                  <h3>{trackName}</h3>
                  <p>Instructors: {instructorName}</p>
                </div>
              </div>
              <p className="track-share-description">{description}</p>
            </div>
          </article>
        </section>

        <section className="missing-panel track-share-actions-panel">
          <h2>Share</h2>
          <div className="missing-form-grid one-col">
            <label>Public Link<input value={link} readOnly /></label>
            <div className="track-share-actions">
              <button type="button" className="missing-btn primary" onClick={handleShare} disabled={!activeTrackId}>
                <Share2 size={16} /> Share
              </button>
              <button type="button" className="missing-btn" onClick={copyLink} disabled={!activeTrackId}>
                <Copy size={16} /> Copy
              </button>
              <button type="button" className="missing-btn ghost" onClick={openTrack} disabled={!activeTrackId}>
                <ExternalLink size={16} /> Open
              </button>
            </div>
            {shareMessage ? <small className="track-share-message">{shareMessage}</small> : null}
          </div>
        </section>
      </div>
    </Shell>
  );
};

export const GlobalLeaderboardPage = () => {
  const { tracks } = useTrackScope();
  const [rows, setRows] = useState([]); const [search, setSearch] = useState(""); const [trackId, setTrackId] = useState(""); const [error, setError] = useState("");
  const load = async () => { setError(""); try { const { data } = await tracksApi.globalLeaderboard({ search: search || undefined, trackId: trackId || undefined }); setRows(data?.leaderboard || []); } catch (e) { setError(getApiErrorMessage(e, "Failed to load leaderboard")); } };
  useEffect(() => { (async () => { try { const { data } = await tracksApi.globalLeaderboard({}); setRows(data?.leaderboard || []); } catch (e) { setError(getApiErrorMessage(e, "Failed to load leaderboard")); } })(); }, []);

  return (
    <Shell title="Global Leaderboard" subtitle="Rankings across all students." actions={<button className="missing-btn ghost" onClick={load}>Refresh</button>}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <section className="missing-panel">
        <div className="missing-toolbar"><div className="missing-search-wrap"><Search size={16} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name" /></div><select className="form-select" style={{ maxWidth: 240 }} value={trackId} onChange={(e) => setTrackId(e.target.value)}><option value="">All Tracks</option>{tracks.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}</select><button className="missing-btn" onClick={load}>Apply</button></div>
        <div className="missing-table-wrap"><table className="missing-table"><thead><tr><th>Rank</th><th>Name</th><th>Points</th></tr></thead><tbody>{rows.length ? rows.map((r) => <tr key={r.studentId}><td>#{r.rank}</td><td>{r.fullName}</td><td>{r.points}</td></tr>) : <tr><td colSpan="3" className="text-center text-muted py-4">No rows.</td></tr>}</tbody></table></div>
      </section>
    </Shell>
  );
};

export const AllAnnouncementsPage = () => {
  const [searchParams] = useSearchParams();
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "accessible" });
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const attachmentItems = useMemo(
    () => normalizeAnnouncementAttachmentItems(selectedAnnouncement?.attachments || []),
    [selectedAnnouncement]
  );

  useEffect(() => {
    const queryTrackId = searchParams.get("trackId");
    if (!queryTrackId || !tracks.length) return;
    if (tracks.some((track) => track._id === queryTrackId) && queryTrackId !== activeTrackId) {
      setActiveTrackId(queryTrackId);
    }
  }, [activeTrackId, searchParams, tracks]);

  useEffect(() => {
    (async () => {
      setError("");
      setNotice("");
      if (!activeTrackId) {
        setRows([]);
        setNotice("No track selected or available.");
        return;
      }

      try {
        const { data } = await tracksApi.announcements(activeTrackId);
        const list = data?.announcements || [];
        setRows(list);
        if (!list.length) {
          setNotice("No announcements for this track yet.");
        }
      } catch (e) {
        setRows([]);
        setError(getApiErrorMessage(e, "Failed to load announcements"));
      }
    })();
  }, [activeTrackId]);

  return (
    <Shell title="All Announcements" subtitle="Track announcements in one place." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {notice ? <div className="alert alert-warning py-2">{notice}</div> : null}
      <section className="missing-panel">
        <div className="missing-feed">
          {rows.length ? rows.map((announcement) => (
            <article key={announcement._id} className="missing-feed-item announcement-clickable" onClick={() => setSelectedAnnouncement(announcement)}>
              <div>
                <h3>{announcement.title}</h3>
                <p className="announcement-preview-text">{stripMarkdown(announcement.content) || "No content."}</p>
                <small>{formatDateTime(announcement.createdAt)}</small>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                {Array.isArray(announcement.links) && announcement.links.length ? <Badge status={`${announcement.links.length} link(s)`} /> : null}
                {Array.isArray(announcement.attachments) && announcement.attachments.length ? <Badge status={`${announcement.attachments.length} file(s)`} /> : null}
                <Badge status="Announcement" />
              </div>
            </article>
          )) : null}
        </div>
      </section>

      {selectedAnnouncement ? (
        <div className="announcement-details-backdrop" onClick={() => setSelectedAnnouncement(null)}>
          <div className="announcement-details-card" onClick={(e) => e.stopPropagation()}>
            <header className="announcement-details-head">
              <div>
                <h3>{selectedAnnouncement.title}</h3>
                <small>{formatDateTime(selectedAnnouncement.createdAt)}</small>
              </div>
              <button type="button" className="missing-btn tiny ghost" onClick={() => setSelectedAnnouncement(null)}>Close</button>
            </header>

            <section className="announcement-details-section">
              <h4>Content</h4>
              <MarkdownPreview content={selectedAnnouncement.content} emptyText="No content." />
            </section>

            {Array.isArray(selectedAnnouncement.links) && selectedAnnouncement.links.length ? (
              <section className="announcement-details-section">
                <h4>Links</h4>
                <ul className="announcement-links-list">
                  {selectedAnnouncement.links.map((linkValue, idx) => (
                    <li key={`${linkValue}-${idx}`}>
                      <a href={resolveMediaUrl(linkValue)} target="_blank" rel="noreferrer">{resolveMediaUrl(linkValue)}</a>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {attachmentItems.length ? (
              <section className="announcement-details-section">
                <h4>Attachments</h4>
                <ul className="announcement-links-list">
                  {attachmentItems.map((attachmentItem, idx) => (
                    <li key={`${attachmentItem.openUrl || attachmentItem.name}-${idx}`}>
                      {attachmentItem.openUrl ? (
                        <a href={attachmentItem.openUrl} target="_blank" rel="noreferrer">{attachmentItem.name}</a>
                      ) : (
                        <span>{attachmentItem.name}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        </div>
      ) : null}
    </Shell>
  );
};

export const TasksPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "accessible" });
  const [rows, setRows] = useState([]);
  const [submissionByTask, setSubmissionByTask] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [taskDetailsDialog, setTaskDetailsDialog] = useState(null);

  useEffect(() => {
    (async () => {
      if (!activeTrackId) {
        setRows([]);
        setSubmissionByTask({});
        return;
      }

      setLoading(true);
      setError("");
      try {
        const { data } = await tracksApi.tasks(activeTrackId);
        const list = data?.tasks || [];
        setRows(list);

        if (user?.role === "student" && list.length) {
          const pairs = await Promise.all(
            list.map(async (task) => {
              try {
                const submissionRes = await submissionsApi.mySubmission(task._id);
                return [task._id, submissionRes.data?.submission || null];
              } catch {
                return [task._id, null];
              }
            })
          );
          setSubmissionByTask(Object.fromEntries(pairs));
        } else {
          setSubmissionByTask({});
        }
      } catch (e) {
        setRows([]);
        setSubmissionByTask({});
        setError(getApiErrorMessage(e, "Failed to load tasks"));
      } finally {
        setLoading(false);
      }
    })();
  }, [activeTrackId, user?.role]);

  const formatRelativeTime = (milliseconds) => {
    const ms = Math.max(0, Number(milliseconds) || 0);
    const totalMinutes = Math.floor(ms / 60000);
    if (totalMinutes < 1) return "less than 1 min";
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const mins = totalMinutes % 60;
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getDeadlineMeta = (deadlineValue) => {
    if (!deadlineValue) {
      return { badge: "No Deadline", detail: "No due date is set.", className: "status-neutral" };
    }

    const deadlineMs = new Date(deadlineValue).getTime();
    const now = Date.now();
    if (Number.isNaN(deadlineMs)) {
      return { badge: "Invalid Deadline", detail: "Deadline format is invalid.", className: "status-neutral" };
    }

    if (deadlineMs <= now) {
      return {
        badge: "Late",
        detail: `Passed ${formatRelativeTime(now - deadlineMs)} ago`,
        className: "status-danger",
      };
    }

    const remainingMs = deadlineMs - now;
    const className = remainingMs <= 24 * 60 * 60 * 1000 ? "status-warn" : "status-good";
    return {
      badge: remainingMs <= 24 * 60 * 60 * 1000 ? "Due Soon" : "In Progress",
      detail: `Remaining ${formatRelativeTime(remainingMs)}`,
      className,
    };
  };

  const getActionLabel = (task, submission) => {
    if (user?.role !== "student") return "Review";
    if (!submission) return "Submit";
    if (task?.allowResubmission) return "View";
    return "View";
  };

  const openTask = (taskId) => {
    if (!activeTrackId || !taskId) return;
    setActiveTrackId(activeTrackId);
    setActiveTaskId(taskId);
    if (user?.role === "student") {
      navigate(`/assignment?trackId=${activeTrackId}&taskId=${taskId}`);
      return;
    }
    navigate(`/grading-interface?taskId=${taskId}`);
  };

  const openTaskDetails = (task, submission, deadlineMeta, canResubmit) => {
    setTaskDetailsDialog({
      task,
      submission,
      deadlineMeta,
      canResubmit,
    });
  };

  return (
    <Shell title="Tasks" subtitle="Assignments for selected track." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {loading ? <div className="alert alert-info py-2">Loading tasks...</div> : null}
      <section className="missing-card-grid">
        {rows.length ? rows.map((task) => {
          const deadlineMeta = getDeadlineMeta(task.deadline);
          const submission = submissionByTask[task._id] || null;
          const isStudent = user?.role === "student";
          const isSubmitted = Boolean(submission);
          const canResubmit = !isSubmitted || Boolean(task.allowResubmission);
          const isGraded = submission?.status === "graded";
          const taskScore = Number(submission?.score || 0);
          const taskBonus = Number(submission?.bonusPoints || 0);
          const gradeTotal = taskScore + taskBonus;
          const hasFeedback = Boolean(`${submission?.feedback || ""}`.trim());
          let overlayMeta = null;
          if (isStudent && isSubmitted) {
            if (isGraded) {
              overlayMeta = {
                variant: "graded",
                title: `Graded: ${gradeTotal}/${task?.points || 0}`,
                subtitle: hasFeedback ? "Feedback available" : "",
              };
            } else if (!canResubmit) {
              overlayMeta = {
                variant: "submitted",
                title: "Final Submission",
                subtitle: "",
              };
            }
          }

          return (
            <article key={task._id} className={`missing-task-card task-compact-card${overlayMeta ? " has-task-overlay" : ""}`}>
              {overlayMeta ? (
                <div className={`task-state-overlay overlay-${overlayMeta.variant}`}>
                  <strong>{overlayMeta.title}</strong>
                  {overlayMeta.subtitle ? <span>{overlayMeta.subtitle}</span> : null}
                </div>
              ) : null}
              <div className="task-compact-head">
                <h3>{task.title}</h3>
                <span className="task-points-pill">{task.points} pts</span>
              </div>

              <p>{stripMarkdown(task.description) || "No description."}</p>

              <div className="task-compact-meta">
                <span className={`missing-status ${deadlineMeta.className}`}>{deadlineMeta.badge}</span>
                <span className="task-compact-meta-text">{deadlineMeta.detail}</span>
                <span className="task-compact-meta-text text-capitalize">Difficulty: {task?.difficulty || "-"}</span>
              </div>

              <div className="task-footer-row">
                <div className="task-status-line">
                  {isStudent && isSubmitted ? <Badge status={submission?.status === "graded" ? "Graded" : "Submitted"} /> : null}
                  {isStudent && isSubmitted && !canResubmit ? <Badge status="Final Submission" /> : null}
                </div>
                <div className="task-compact-actions">
                  <button className="missing-btn tiny ghost" onClick={() => openTaskDetails(task, submission, deadlineMeta, canResubmit)}>
                    Details
                  </button>
                  {isStudent && isGraded && hasFeedback ? (
                    <button className="missing-btn tiny" onClick={() => openTaskDetails(task, submission, deadlineMeta, canResubmit)}>
                      Feedback
                    </button>
                  ) : null}
                  <button className="missing-btn tiny" onClick={() => openTask(task._id)}>
                    {getActionLabel(task, submission)}
                  </button>
                </div>
              </div>
            </article>
          );
        }) : <div className="text-muted">No tasks.</div>}
      </section>

      {taskDetailsDialog ? (
        <div className="task-details-backdrop" onClick={() => setTaskDetailsDialog(null)}>
          <div className="task-details-modal" onClick={(e) => e.stopPropagation()}>
            <header className="task-details-head">
              <div>
                <h3>{taskDetailsDialog.task?.title || "Task details"}</h3>
                <small>{formatDateTime(taskDetailsDialog.task?.deadline)}</small>
              </div>
              <button type="button" className="missing-btn tiny ghost" onClick={() => setTaskDetailsDialog(null)}>Close</button>
            </header>

            <div className="task-details-grid">
              <article className="task-detail-item">
                <small>Deadline</small>
                <strong>{taskDetailsDialog.deadlineMeta?.badge || "No deadline"}</strong>
                <span>{taskDetailsDialog.deadlineMeta?.detail || "No due date."}</span>
              </article>
              <article className="task-detail-item">
                <small>Submission</small>
                <strong>{taskDetailsDialog.submission ? "Submitted" : "Not submitted"}</strong>
                <span>
                  {taskDetailsDialog.submission
                    ? `Last update: ${formatDateTime(taskDetailsDialog.submission?.submittedAt)}`
                    : "You have not submitted this task yet."}
                </span>
              </article>
              <article className="task-detail-item">
                <small>Resubmission</small>
                <strong>{taskDetailsDialog.canResubmit ? "Allowed" : "Not allowed"}</strong>
                <span>{taskDetailsDialog.canResubmit ? "You can submit again." : "Final submission only."}</span>
              </article>
              <article className="task-detail-item">
                <small>Submission Type</small>
                <strong>{taskDetailsDialog.task?.submissionType || "-"}</strong>
                <span className="text-capitalize">Difficulty: {taskDetailsDialog.task?.difficulty || "-"}</span>
              </article>
              {taskDetailsDialog.submission?.status === "graded" ? (
                <article className="task-detail-item task-grade-item full-width">
                  <small>Your Grade</small>
                  <strong>
                    {Number(taskDetailsDialog.submission?.score || 0) + Number(taskDetailsDialog.submission?.bonusPoints || 0)}
                    {" / "}
                    {taskDetailsDialog.task?.points || 0}
                  </strong>
                  <span>
                    {Number(taskDetailsDialog.submission?.bonusPoints || 0) > 0
                      ? `Includes bonus: +${Number(taskDetailsDialog.submission?.bonusPoints || 0)}`
                      : "Graded by instructor."}
                  </span>
                </article>
              ) : null}
            </div>

            {`${taskDetailsDialog.submission?.feedback || ""}`.trim() ? (
              <section className="task-details-feedback">
                <h4>Instructor Feedback</h4>
                <p>{taskDetailsDialog.submission?.feedback}</p>
              </section>
            ) : null}

            <section className="task-details-description">
              <h4>Description</h4>
              <MarkdownPreview content={taskDetailsDialog.task?.description} emptyText="No description." />
            </section>
          </div>
        </div>
      ) : null}
    </Shell>
  );
};

export const TasksSubmittedPage = () => {
  const [searchParams] = useSearchParams();
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    (async () => {
      setError("");
      setNotice("");
      setRows([]);
      if (!activeTrackId) {
        setTaskOptions([]);
        setSelectedTaskId("");
        setNotice("No track assigned to this instructor account.");
        return;
      }
      try {
        const { data } = await tracksApi.tasks(activeTrackId);
        const list = data?.tasks || [];
        setTaskOptions(list);
        if (!list.length) {
          setSelectedTaskId("");
          setNotice("No tasks found in this track yet.");
          return;
        }
        const preselectedId = searchParams.get("taskId");
        const selected = list.find((task) => task._id === preselectedId) || list[0];
        setSelectedTaskId(selected._id);
      } catch (e) {
        setTaskOptions([]);
        setSelectedTaskId("");
        setError(getApiErrorMessage(e, "Failed to load tasks for selected track"));
      }
    })();
  }, [activeTrackId, searchParams]);

  useEffect(() => {
    (async () => {
      if (!selectedTaskId) return;
      setError("");
      try {
        const { data } = await submissionsApi.instructorTaskSubmissions(selectedTaskId);
        setRows(data?.submissions || []);
      } catch (e) {
        setRows([]);
        setError(getApiErrorMessage(e, "Failed to load submissions"));
      }
    })();
  }, [selectedTaskId]);

  return (
    <Shell title="Tasks Submitted" subtitle="Submitted assignments by task." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <section className="missing-panel"><label>Task<select className="form-select" value={selectedTaskId} onChange={(e) => setSelectedTaskId(e.target.value)}>{taskOptions.map((t) => <option key={t._id} value={t._id}>{t.title}</option>)}</select></label></section>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      {notice ? <div className="alert alert-warning py-2">{notice}</div> : null}
      <section className="missing-panel"><div className="missing-table-wrap"><table className="missing-table"><thead><tr><th>Student</th><th>Status</th><th>Submitted At</th><th>File</th><th>Link</th><th>Score</th></tr></thead><tbody>{rows.length ? rows.map((r) => <tr key={r._id}><td>{r.student?.fullName}</td><td><Badge status={r.status} /></td><td>{formatDateTime(r.submittedAt)}</td><td>{resolveMediaUrl(r.fileUrl) ? <a href={resolveMediaUrl(r.fileUrl)} target="_blank" rel="noreferrer">Open File</a> : "-"}</td><td>{resolveMediaUrl(r.link) ? <a href={resolveMediaUrl(r.link)} target="_blank" rel="noreferrer">Open Link</a> : "-"}</td><td>{r.score || 0}</td></tr>) : <tr><td colSpan="6" className="text-center text-muted py-4">No submissions.</td></tr>}</tbody></table></div></section>
    </Shell>
  );
};

export const GradingInterfacePage = () => {
  const [searchParams] = useSearchParams();
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [taskOptions, setTaskOptions] = useState([]);
  const [selectedTaskId, setSelectedTaskId] = useState("");
  const [submissions, setSubmissions] = useState([]);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState("");
  const [score, setScore] = useState(0);
  const [bonusEnabled, setBonusEnabled] = useState(false);
  const [bonusPoints, setBonusPoints] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [notice, setNotice] = useState("");

  const selectedTask = useMemo(
    () => taskOptions.find((task) => task._id === selectedTaskId) || null,
    [selectedTaskId, taskOptions]
  );
  const selectedSubmission = useMemo(
    () => submissions.find((submission) => submission._id === selectedSubmissionId) || null,
    [selectedSubmissionId, submissions]
  );
  const resolvedSubmissionFileUrl = useMemo(
    () => resolveMediaUrl(selectedSubmission?.fileUrl),
    [selectedSubmission?.fileUrl]
  );
  const resolvedSubmissionLink = useMemo(
    () => resolveMediaUrl(selectedSubmission?.link),
    [selectedSubmission?.link]
  );
  const submissionIndex = useMemo(
    () => submissions.findIndex((submission) => submission._id === selectedSubmissionId),
    [selectedSubmissionId, submissions]
  );
  const maxScore = Number(selectedTask?.points || 100);
  const scorePercent = Math.max(0, Math.min(100, Math.round((Number(score || 0) / Math.max(1, maxScore)) * 100)));
  const lateDays = useMemo(() => {
    if (!selectedSubmission?.submittedAt || !selectedTask?.deadline) return 0;
    const deadline = new Date(selectedTask.deadline).getTime();
    const submitted = new Date(selectedSubmission.submittedAt).getTime();
    if (submitted <= deadline) return 0;
    return Math.ceil((submitted - deadline) / (24 * 60 * 60 * 1000));
  }, [selectedSubmission?.submittedAt, selectedTask?.deadline]);

  useEffect(() => {
    (async () => {
      setError("");
      setNotice("");
      setSubmissions([]);
      setSelectedSubmissionId("");
      if (!activeTrackId) {
        setTaskOptions([]);
        setSelectedTaskId("");
        setNotice("No track assigned to this instructor account.");
        return;
      }
      try {
        const { data } = await tracksApi.tasks(activeTrackId);
        const list = data?.tasks || [];
        setTaskOptions(list);
        if (!list.length) {
          setSelectedTaskId("");
          setNotice("No tasks found in this track yet.");
          return;
        }
        const preselectedId = searchParams.get("taskId");
        const selected = list.find((task) => task._id === preselectedId) || list[0];
        setSelectedTaskId(selected._id);
      } catch (e) {
        setTaskOptions([]);
        setSelectedTaskId("");
        setError(getApiErrorMessage(e, "Failed to load tasks for grading"));
      }
    })();
  }, [activeTrackId, searchParams]);

  useEffect(() => {
    (async () => {
      if (!selectedTaskId) return;
      setError("");
      try {
        const { data } = await submissionsApi.instructorTaskSubmissions(selectedTaskId);
        const list = data?.submissions || [];
        setSubmissions(list);
        if (list.length) {
          setSelectedSubmissionId(list[0]._id);
        } else {
          setSelectedSubmissionId("");
          setScore(0);
          setBonusEnabled(false);
          setBonusPoints(0);
          setFeedback("");
        }
      } catch (e) {
        setSubmissions([]);
        setSelectedSubmissionId("");
        setError(getApiErrorMessage(e, "Failed to load grading data"));
      }
    })();
  }, [selectedTaskId]);

  useEffect(() => {
    if (!selectedSubmission) return;
    const nextScore = Number(selectedSubmission.score || 0);
    const nextBonus = Number(selectedSubmission.bonusPoints || 0);
    setScore(nextScore);
    setBonusPoints(nextBonus);
    setBonusEnabled(nextBonus > 0);
    setFeedback(selectedSubmission.feedback || "");
  }, [selectedSubmission]);

  const saveGrade = async () => {
    setError("");
    setMsg("");
    try {
      await submissionsApi.gradeSubmission(selectedSubmissionId, {
        score: Number(score),
        bonusPoints: bonusEnabled ? Number(bonusPoints) : 0,
        feedback,
      });
      setMsg("Grade submitted successfully.");
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to save grade"));
    }
  };

  return (
    <Shell title="Grading Interface" subtitle="Score submissions and feedback." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <section className="missing-panel">
        <div className="missing-form-grid two-col">
          <label>Task<select value={selectedTaskId} onChange={(e) => setSelectedTaskId(e.target.value)}>{taskOptions.map((t) => <option key={t._id} value={t._id}>{t.title}</option>)}</select></label>
          <label>Submission<select value={selectedSubmissionId} onChange={(e) => setSelectedSubmissionId(e.target.value)}>{submissions.map((s) => <option key={s._id} value={s._id}>{s.student?.fullName}</option>)}</select></label>
        </div>
      </section>
      {notice ? <div className="alert alert-warning py-2">{notice}</div> : null}
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}

      <section className="grading-layout">
        <article className="grading-preview">
          <header className="grading-preview-header">
            <div className="grading-preview-title">
              <button type="button" className="grading-icon-btn"><ArrowLeft size={16} /></button>
              <div>
                <h3>{selectedTask?.title || "Task"}</h3>
                <p>
                  Student: {selectedSubmission?.student?.fullName || "-"} • Submitted: {formatDateTime(selectedSubmission?.submittedAt)}
                </p>
              </div>
            </div>
            <div className="grading-preview-actions">
              {lateDays > 0 ? <span className="missing-status status-danger">Late Submission - {lateDays} day(s)</span> : <span className="missing-status status-good">On Time</span>}
              <span className="grading-index">
                <ChevronLeft size={14} />
                {submissions.length ? `${submissionIndex + 1} of ${submissions.length}` : "0 of 0"}
                <ChevronRight size={14} />
              </span>
            </div>
          </header>

          <div className="grading-doc-toolbar">
            <span>{resolvedSubmissionFileUrl ? "SUBMISSION.PDF" : resolvedSubmissionLink ? "LINK SUBMISSION" : "NO SUBMISSION FILE"}</span>
            {resolvedSubmissionFileUrl ? <a href={resolvedSubmissionFileUrl} target="_blank" rel="noreferrer" className="missing-btn tiny"><FileDown size={14} /> Download</a> : null}
          </div>

          <div className="grading-doc-body">
            {resolvedSubmissionFileUrl ? (
              <iframe title="Submission Preview" src={resolvedSubmissionFileUrl} className="grading-preview-frame" />
            ) : resolvedSubmissionLink ? (
              <a href={resolvedSubmissionLink} target="_blank" rel="noreferrer" className="grading-link-card">
                Open Submitted Link
              </a>
            ) : (
              <div className="grading-placeholder">
                <div className="grading-line lg"></div>
                <div className="grading-line"></div>
                <div className="grading-line"></div>
                <div className="grading-box"></div>
                <div className="grading-line"></div>
              </div>
            )}
          </div>
        </article>

        <aside className="grading-sidebar">
          <section className="grading-side-card">
            <div className="grading-side-head">
              <h4>Total Score</h4>
              <span>Autosaved</span>
            </div>
            <div className="grading-score-row">
              <input type="number" min="0" max={maxScore} value={score} onChange={(e) => setScore(e.target.value)} />
              <b>/ {maxScore}</b>
            </div>
            <div className="grading-meter"><span style={{ width: `${scorePercent}%` }} /></div>
            <div className="grading-meter-labels"><small>Poor</small><small>Passing</small><small>Excellent</small></div>
          </section>

          <section className="grading-side-card">
            <h4><SlidersHorizontal size={15} /> Rubric Criteria</h4>
            <div className="grading-rubric-item"><div><b>Content Accuracy</b><small>Key concepts covered</small></div><span>{Math.round(Number(score || 0) * 0.7)}/{Math.round(maxScore * 0.7)}</span></div>
            <div className="grading-rubric-item"><div><b>Formatting</b><small>Style adherence</small></div><span>{Math.round(Number(score || 0) * 0.3)}/{Math.round(maxScore * 0.3)}</span></div>
          </section>

          <section className="grading-side-card">
            <h4><Award size={15} /> Bonus Points</h4>
            <label className="grading-toggle-row"><span>Enable bonus</span><input type="checkbox" checked={bonusEnabled} onChange={(e) => setBonusEnabled(e.target.checked)} /></label>
            <input type="number" min="0" value={bonusPoints} onChange={(e) => setBonusPoints(e.target.value)} disabled={!bonusEnabled} />
          </section>

          <section className="grading-side-card">
            <h4><MessageSquareText size={15} /> Instructor Feedback</h4>
            <textarea rows="6" value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder="Write detailed feedback for the student..." />
            {msg ? <div className="alert alert-success py-2 mt-2 mb-0">{msg}</div> : null}
          </section>

          <button className="missing-btn primary grading-submit-btn" onClick={saveGrade} disabled={!selectedSubmissionId}>
            Submit Grade
          </button>
        </aside>
      </section>
    </Shell>
  );
};

export const CourseBasicsPage = () => {
  const { tracks, activeTrackId, activeTrack, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [name, setName] = useState(""); const [description, setDescription] = useState(""); const [status, setStatus] = useState("open"); const [error, setError] = useState(""); const [msg, setMsg] = useState("");
  useEffect(() => { setName(activeTrack?.name || ""); setDescription(activeTrack?.description || ""); setStatus(activeTrack?.status || "open"); }, [activeTrack]);
  const save = async () => { setError(""); setMsg(""); try { await adminApi.updateTrack(activeTrackId, { name, description, status }); setMsg("Basics updated."); } catch (e) { setError(getApiErrorMessage(e, "Failed to save basics")); } };
  return (
    <Shell title="Course Basics" subtitle="Update basic track metadata." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <section className="missing-panel"><h2><BookOpen size={18} /> Basic Information</h2><div className="missing-form-grid two-col"><label>Title<input value={name} onChange={(e) => setName(e.target.value)} /></label><label>Status<select value={status} onChange={(e) => setStatus(e.target.value)}><option value="open">open</option><option value="closed">closed</option></select></label><label className="full-width">Description<textarea rows="6" value={description} onChange={(e) => setDescription(e.target.value)} /></label></div>{error ? <div className="alert alert-danger py-2 mt-3">{error}</div> : null}{msg ? <div className="alert alert-success py-2 mt-3">{msg}</div> : null}<button className="missing-btn primary mt-3" onClick={save}>Save Draft</button></section>
    </Shell>
  );
};

export const CourseSettingsPage = () => {
  const { tracks, activeTrackId, activeTrack, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [status, setStatus] = useState("open"); const [error, setError] = useState(""); const [msg, setMsg] = useState("");
  useEffect(() => { setStatus(activeTrack?.status || "open"); }, [activeTrack]);
  const save = async () => { setError(""); setMsg(""); try { await adminApi.updateTrack(activeTrackId, { status }); setMsg("Settings saved."); } catch (e) { setError(getApiErrorMessage(e, "Failed to save settings")); } };
  return (
    <Shell title="Course Settings" subtitle="Control track open/closed mode." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <div className="missing-two-col"><section className="missing-panel"><div className="missing-option-list"><label className="option-row"><input type="radio" checked={status === "open"} onChange={() => setStatus("open")} /><div><b>Open</b><p>Enrollment available.</p></div></label><label className="option-row"><input type="radio" checked={status === "closed"} onChange={() => setStatus("closed")} /><div><b>Closed</b><p>Enrollment disabled.</p></div></label></div></section><section className="missing-panel"><h2>Status</h2><Badge status={status} />{error ? <div className="alert alert-danger py-2 mt-3">{error}</div> : null}{msg ? <div className="alert alert-success py-2 mt-3">{msg}</div> : null}<button className="missing-btn primary mt-3" onClick={save}><Settings2 size={16} /> Save</button></section></div>
    </Shell>
  );
};

export const CourseCurriculumPage = () => {
  const { tracks, activeTrackId, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [rows, setRows] = useState([]); const [error, setError] = useState("");
  useEffect(() => { (async () => { if (!activeTrackId) return; try { const { data } = await tracksApi.tasks(activeTrackId); setRows(data?.tasks || []); } catch (e) { setError(getApiErrorMessage(e, "Failed to load curriculum")); } })(); }, [activeTrackId]);
  return (
    <Shell title="Course Curriculum" subtitle="Curriculum mapped to tasks." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      {error ? <div className="alert alert-danger py-2">{error}</div> : null}
      <section className="missing-panel">{rows.length ? rows.map((r) => <div key={r._id} className="module-card"><div className="module-head"><h3>{r.title}</h3><Badge status={r.difficulty} /></div><ul><li>{r.description || "No description"}</li><li>Deadline: {formatDateTime(r.deadline)}</li><li>Points: {r.points}</li></ul></div>) : <div className="text-muted">No curriculum items.</div>}</section>
    </Shell>
  );
};

export const CoursePreviewPage = () => {
  const { tracks, activeTrackId, activeTrack, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [tasksCount, setTasksCount] = useState(0); const [annCount, setAnnCount] = useState(0);
  useEffect(() => { (async () => { if (!activeTrackId) return; try { const [t, a] = await Promise.all([tracksApi.tasks(activeTrackId), tracksApi.announcements(activeTrackId)]); setTasksCount((t.data?.tasks || []).length); setAnnCount((a.data?.announcements || []).length); } catch { setTasksCount(0); setAnnCount(0); } })(); }, [activeTrackId]);
  const previewBannerUrl = resolveMediaUrl(activeTrack?.bannerUrl);
  const previewImageStyle = previewBannerUrl ? { backgroundImage: `url(${previewBannerUrl})`, backgroundSize: "cover", backgroundPosition: "center" } : undefined;
  return (
    <Shell title="Course Preview" subtitle="Preview learner-facing track card." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <div className="missing-two-col"><section className="missing-panel"><article className="share-preview-card"><div className="share-preview-image" style={previewImageStyle} /><div className="share-preview-content"><Badge status={activeTrack?.status || "open"} /><h3>{activeTrack?.name || "Track"}</h3><p>{activeTrack?.description || "No description."}</p><div className="share-metadata"><span>{tasksCount} task(s)</span><span>{annCount} announcement(s)</span></div></div></article></section><section className="missing-panel"><h2>Checklist</h2><ul className="missing-checklist"><li><CheckCircle2 size={16} /> Basics ready</li><li><CheckCircle2 size={16} /> Curriculum ready</li><li><Clock3 size={16} /> Final publish pending</li></ul></section></div>
    </Shell>
  );
};

export const CourseFinalPage = () => {
  const { tracks, activeTrackId, activeTrack, onTrackChange, loadingTracks } = useTrackScope({ mode: "owned" });
  const [error, setError] = useState(""); const [msg, setMsg] = useState("");
  const setStatus = async (status) => { setError(""); setMsg(""); try { await adminApi.updateTrack(activeTrackId, { status }); setMsg(`Track status updated to ${status}.`); } catch (e) { setError(getApiErrorMessage(e, "Failed to update track status")); } };
  return (
    <Shell title="Course Final" subtitle="Finalize publication state." actions={<TrackPicker tracks={tracks} value={activeTrackId} onChange={onTrackChange} disabled={loadingTracks || !tracks.length} />}>
      <div className="missing-two-col"><section className="missing-panel"><h2><NotebookPen size={18} /> Final Validation</h2><ul className="missing-checklist"><li><CheckCircle2 size={16} /> Metadata reviewed</li><li><CheckCircle2 size={16} /> Curriculum ready</li><li><Clock3 size={16} /> Current status: {activeTrack?.status || "-"}</li></ul></section><section className="missing-panel">{error ? <div className="alert alert-danger py-2">{error}</div> : null}{msg ? <div className="alert alert-success py-2">{msg}</div> : null}<div className="missing-option-list"><button className="missing-btn primary" onClick={() => setStatus("open")}><Send size={16} /> Publish Course</button><button className="missing-btn danger" onClick={() => setStatus("closed")}><XCircle size={16} /> Close Enrollment</button></div></section></div>
    </Shell>
  );
};
